import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import HighStock from 'highcharts/highstock';
import type { Options as HighchartsOptions, GradientColorObject, PatternObject } from 'highcharts';
import HCAccessibility from 'highcharts/modules/accessibility';
import HighchartsReact from 'highcharts-react-official';
import HCExporting from 'highcharts/modules/exporting';
import HCOfflineExporting from 'highcharts/modules/offline-exporting';
import HCNoData from 'highcharts/modules/no-data-to-display';
import styled from 'styled-components';
import reactGA from 'react-ga';
import { lineChartHighchartsTooltipCfg } from '~/shared/utils/lineChartTooltip';
import {
  deriveNonNegativeAxisFloor,
  deriveAxisLimits,
  normalizePoint,
  readFiniteXExtentsFromAxis,
  xExtentsDiffer,
  pickXExtentsFromAfterSetExtremesEvent,
  shouldPersistUserXExtentsFromEvent,
  type PointLike,
} from '~/shared/utils/chartAxis';
import { resolveColorToken } from '~/shared/utils';
import { getLineChartExportMenuItems, registerLineChartExport } from '~/shared/utils/lineChartExport';
import { buildChartExportFileName } from '~/shared/utils/buildChartExportFileName';
import { syncLineChartLegendTooltips } from '~/shared/utils/lineChartLegendTooltip';
import {
  applyLineChartSelectionToChart,
  syncLineChartSelectedSeriesFromChart,
  useLineChartLegendClickHandlers,
  scheduleLineChartSelectedSeriesSync,
} from '~/shared/utils/lineChartSelectionSync';
import {
  appendDoubleYAxisChartSeries,
  buildDoubleYAxisLegendOptions,
  doubleYAxisLegendLabelFormatter,
  isLegendAxisDivider,
  maintainDoubleYAxisInlineLegendDivider,
  resolveInitialDoubleYAxisVisibleNames,
  resolveDoubleYAxisChartLayout,
} from '~/shared/utils/doubleYAxisLegend';
import {
  applyRangeSelectorToChart,
  areChartXExtremesCanonicalForRangeIndex,
  DEFAULT_RANGE_SELECTOR_INDEX,
  extractSeriesXValues,
  MAX_RANGE_SELECTOR_INDEX,
  readMainAxisMaxForDurationPreset,
} from '~/shared/utils/timeWindow';

HCExporting(HighStock);
HCNoData(HighStock);
HCOfflineExporting(HighStock);
HCAccessibility(HighStock);
registerLineChartExport(HighStock);

const EMPTY_STOCK_OPTIONS: HighchartsOptions = { series: [] };

export interface IStackBarChart {
  axisXLabels: string[];
  axisYLabels: string[];
  values: { XLabel: string; YLabel: string; Value: number }[];
  matrix: number[][];
  data?: any;
}

const Root = styled.div<{ $gridheight: number }>`
  height: ${({ $gridheight }) => ($gridheight ? $gridheight * 100 : 400)}px;
  width: 100%;
  position: relative;
  margin-top: 10px;
`;

const HighchartContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  border: solid 1px var(--border-chart);
  background: var(--background-chart);
  padding: 10px;
  .highcharts-tooltip span {
    z-index: 9999 !important;
  }
  .tooltip {
    padding: 2px;
    background-color: var(--background-chart-tooltip);
    opacity: 1;
    z-index: 9999 !important;
  }
  .highcharts-legend-item > .highcharts-point {
    transform: unset !important;
  }
  .highcharts-button-box {
    x: 2;
    y: 1;
    rx: 16;
    ry: 16;
    &:hover {
      fill: #d4e5ff !important;
    }
  }
  .highcharts-button-symbol {
    width: 3.5px !important;
    fill: #4c5e72 !important;
    stroke: transparent !important;
    pointer-events: none;
  }
  .highcharts-legend-item-hidden {
    text {
      text-decoration: none !important;
      opacity: 0.5 !important;
    }
    path {
      text-decoration: none !important;
      opacity: 0.5 !important;
    }
  }
  .double-y-axis-chart .highcharts-legend-item.double-y-axis-legend-divider-item,
  .double-y-axis-chart .highcharts-legend-item:has(.double-y-axis-legend-divider) {
    pointer-events: none !important;
    opacity: 1 !important;

    &.highcharts-legend-item-hidden,
    &.highcharts-legend-item-hidden text,
    &.highcharts-legend-item-hidden path {
      opacity: 1 !important;
    }

    .highcharts-point,
    > rect,
    > path {
      display: none !important;
    }

    .double-y-axis-legend-divider {
      display: block !important;
      height: 0 !important;
      border-top: 1px solid #99a3af !important;
      margin: 8px 0 !important;
      box-sizing: border-box !important;
    }
  }
`;

HighStock.SVGRenderer.prototype.symbols.ellipsisvertical = function () {
  const path = [
    'M18.1538 16C18.1538 17.1451 17.222 18.0769 16.0769 18.0769C14.9318 18.0769 14 17.1451 14 16C14 14.8549 14.9318 13.9231 16.0769 13.9231C17.222 13.9231 18.1538 14.8549 18.1538 16Z M18.1538 22.9231C18.1538 24.0682 17.222 25 16.0769 25C14.9318 25 14 24.0682 14 22.9231C14 21.778 14.9318 20.8462 16.0769 20.8462C17.222 20.8462 18.1538 21.778 18.1538 22.9231Z M18.1538 9.07692C18.1538 10.222 17.222 11.1538 16.0769 11.1538C14.9318 11.1538 14 10.222 14 9.07692C14 7.93185 14.9318 7 16.0769 7C17.222 7 18.1538 7.93185 18.1538 9.07692Z',
  ];
  return path;
};

type HighchartsColor = string | GradientColorObject | PatternObject;

type ChartSeriesConfig = {
  type: string;
  name?: string;
  data?: PointLike[];
  yAxis?: string;
  color?: HighchartsColor;
  showInNavigator?: boolean;
  showInLegend?: boolean;
  visible?: boolean;
  custom?: {
    description?: string;
  };
};

const PRIMARY_AXIS_ID = 'returns-axis';
const SECONDARY_AXIS_ID = 'sentiment-axis';

/** Main chart only: extra plot width when vertical inline legend is shown (from the right margin). */
const INLINE_VERTICAL_PLOT_EXTRA_WIDTH = 20;
/** Main chart only: right y-axis offset when two series use the horizontal legend. */
const DUAL_SERIES_RIGHT_AXIS_OFFSET = 50;
/** Main chart only: shift right y-axis right when inline legend is shown. */
const INLINE_LEGEND_RIGHT_AXIS_SHIFT = 8;

const DOUBLE_Y_AXIS_LEGEND_CONFIG = {
  leftAxisId: PRIMARY_AXIS_ID,
  rightAxisId: SECONDARY_AXIS_ID,
  defaultAxisId: PRIMARY_AXIS_ID,
};

const normalizeSeriesData = (input?: PointLike[]): Array<[number, number | null]> => {
  if (!Array.isArray(input)) {
    return [];
  }

  return input
    .map(point => {
      const normalized = normalizePoint(point);
      if (!normalized) {
        return null;
      }
      const { x, y } = normalized;
      if (!Number.isFinite(x)) {
        return null;
      }
      if (y !== null && !Number.isFinite(y)) {
        return null;
      }
      return [Number(x), y === null ? null : Number(y)] as [number, number | null];
    })
    .filter((point): point is [number, number | null] => point !== null)
    .sort((a, b) => a[0] - b[0]);
};

let exportMenuIdCounter = 0;

export default function DoubleYAxisLineChart({
  data,
  size,
  prism,
  filterValues,
  colorScheme,
  chartTitle,
  chartSubtitle,
  chartYtitle,
  chartYtitle2,
  tooltipCfg,
  gridHeight,
  isLoading,
  plotBands,
  numberOfLines,
  onTopicExplainabilityRequest,
  topicForExplainability,
  selectedRangeSelector,
  setSelectedRangeSelector,
  selectedSeriesNames: selectedSeriesNamesProp,
  onSelectedSeriesNamesChange,
  onSeriesMetadataChange,
  dropdownLegendEnabled,
  onExportMenuVisibilityChange,
  yAxisRangeMode = 'padded-visible',
  yAxisPaddingRatio = 0.2,
}: any) {
  const chartRef = useRef<any>(null);
  const [chartReadyTick, setChartReadyTick] = useState(0);
  const savedUserXExtentsRef = useRef<{ min: number; max: number } | null>(null);
  const [xRangeUserCustom, setXRangeUserCustom] = useState(false);
  const applyingRangePresetRef = useRef(false);
  const pendingDurationPresetAnchorRef = useRef<number | undefined>(undefined);
  const [internalSelectedSeriesNames, setInternalSelectedSeriesNames] = useState<string[] | null>(null);
  const doneHideRef = useRef(false);
  const selectedSeriesNames = selectedSeriesNamesProp ?? internalSelectedSeriesNames ?? null;
  const updateSelectedSeriesNames = useCallback(
    (next: string[] | null) => {
      if (onSelectedSeriesNamesChange) {
        onSelectedSeriesNamesChange(next);
      } else {
        setInternalSelectedSeriesNames(next);
      }
    },
    [onSelectedSeriesNamesChange],
  );
  const exportMenuClassName = useMemo(() => {
    exportMenuIdCounter += 1;
    return `highcharts-indicator-menu-${exportMenuIdCounter}`;
  }, []);
  const combinedMenuClassName = `highcharts-indicator-menu ${exportMenuClassName}`;
  const containerWidth = size?.width;
  const isNarrowChart = containerWidth && containerWidth < 600;
  const rootStyles = useMemo(
    () => (typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null),
    [],
  );
  const defaultSeriesColors = useMemo<HighchartsColor[]>(() => {
    const highchartsOptions = HighStock.getOptions ? HighStock.getOptions() : null;
    const palette = highchartsOptions?.colors;
    return Array.isArray(palette) ? palette : [];
  }, []);

  const resolvedColorScheme = useMemo<(string | undefined)[]>(() => {
    if (!Array.isArray(colorScheme) || colorScheme.length === 0) {
      return [];
    }

    return colorScheme.map(rawColor => {
      if (typeof rawColor !== 'string') {
        return undefined;
      }

      const resolved = resolveColorToken(rawColor, rootStyles) ?? rawColor;
      if (typeof resolved === 'string') {
        const trimmed = resolved.trim();
        if (trimmed) {
          return trimmed;
        }
      }

      const trimmedRaw = rawColor.trim();
      return trimmedRaw || undefined;
    });
  }, [colorScheme, rootStyles]);

  const resolveSeriesColor = useCallback(
    (inputColor: string | null | undefined, fallbackIndex: number): HighchartsColor | undefined => {
      const fallbackColorFromScheme = resolvedColorScheme[fallbackIndex];
      const fallbackColor = fallbackColorFromScheme ?? defaultSeriesColors[fallbackIndex];

      if (typeof inputColor !== 'string') {
        return fallbackColor;
      }

      const resolved = resolveColorToken(inputColor, rootStyles) ?? inputColor;
      const normalized = typeof resolved === 'string' ? resolved.trim() : '';
      if (normalized) {
        return normalized;
      }

      return fallbackColor;
    },
    [defaultSeriesColors, resolvedColorScheme, rootStyles],
  );

  const rawSeries = useMemo(() => {
    const seriesList: ChartSeriesConfig[] = [];
    const primarySeriesList = Array.isArray(data?.seriesData1) ? data.seriesData1 : [];

    primarySeriesList.forEach((primary: any, index: number) => {
      if (!primary) return;
      const resolvedPrimaryColor = resolveSeriesColor(primary.color, index);
      const primarySeries: ChartSeriesConfig = {
        type: 'spline',
        name: primary.name,
        data: normalizeSeriesData(primary.data as PointLike[] | undefined),
        yAxis: PRIMARY_AXIS_ID,
        showInNavigator: primary.showInNavigator !== false,
      };

      if (resolvedPrimaryColor) {
        primarySeries.color = resolvedPrimaryColor;
      }
      if (primary.custom?.description) {
        primarySeries.custom = { description: primary.custom.description };
      }

      seriesList.push(primarySeries);
    });

    const secondarySeriesList = Array.isArray(data?.seriesData2) ? data.seriesData2 : [];
    const primaryCount = primarySeriesList.length;

    secondarySeriesList.forEach((secondary: any, index: number) => {
      if (!secondary) return;
      const resolvedSecondaryColor = resolveSeriesColor(secondary.color, primaryCount + index);
      const secondarySeries: ChartSeriesConfig = {
        type: 'spline',
        name: secondary.name,
        data: normalizeSeriesData(secondary.data as PointLike[] | undefined),
        yAxis: SECONDARY_AXIS_ID,
        showInNavigator: secondary.showInNavigator !== false,
      };

      if (resolvedSecondaryColor) {
        secondarySeries.color = resolvedSecondaryColor;
      }
      if (secondary.custom?.description) {
        secondarySeries.custom = { description: secondary.custom.description };
      }

      seriesList.push(secondarySeries);
    });

    return seriesList;
  }, [data, resolveSeriesColor]);

  const baseSeriesNames = useMemo(
    () => rawSeries.map(series => series?.name).filter((name): name is string => Boolean(name)),
    [rawSeries],
  );

  const normalizeSelection = useCallback(
    (selection: string[] | null | undefined) => {
      if (selection === null || selection === undefined) {
        return Array.from(
          resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
            numberOfLines,
            prism,
          }),
        );
      }
      const selectionSet = new Set(selection);
      return baseSeriesNames.filter(name => selectionSet.has(name));
    },
    [baseSeriesNames, rawSeries, numberOfLines, prism],
  );

  const areSelectionsEqual = useCallback(
    (a: string[] | null | undefined, b: string[] | null | undefined) => {
      if (a === b) return true;
      const normalizedA = normalizeSelection(a);
      const normalizedB = normalizeSelection(b);
      if (normalizedA.length !== normalizedB.length) {
        return false;
      }
      return normalizedA.every((value, index) => normalizedB[index] === value);
    },
    [normalizeSelection],
  );

  const exportFileName = buildChartExportFileName(chartTitle, filterValues, prism);

  const updateSelectedSeriesFromChart = useCallback(
    (chartInstance?: HighStock.Chart, revealedSeriesName?: string, allowCollapseToEmpty = false) => {
      if (dropdownLegendEnabled) {
        return;
      }

      const chart = chartInstance ?? chartRef.current;
      if (!chart) return;

      syncLineChartSelectedSeriesFromChart({
        chart,
        baseSeriesNames,
        selectedSeriesNames,
        areSelectionsEqual,
        setSelectedSeriesNames: updateSelectedSeriesNames,
        revealedSeriesName,
        allowCollapseToEmpty,
        collapseFullSelectionToNull: false,
      });
    },
    [areSelectionsEqual, baseSeriesNames, dropdownLegendEnabled, selectedSeriesNames, updateSelectedSeriesNames],
  );

  const resolveVisibleNamesWhenNull = useCallback(
    () =>
      Array.from(
        resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
          numberOfLines,
          prism,
        }),
      ),
    [rawSeries, numberOfLines, prism],
  );

  const { legendEventsItemClick } = useLineChartLegendClickHandlers({
    baseSeriesNames,
    selectedSeriesNames,
    setSelectedSeriesNames: updateSelectedSeriesNames,
    resolveVisibleNamesWhenNull,
    collapseFullSelectionToNull: false,
  });

  const layoutSeriesCount = useMemo(() => baseSeriesNames.length, [baseSeriesNames.length]);

  const visibleSeriesNames = useMemo(
    () =>
      resolveInitialDoubleYAxisVisibleNames(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG, {
        numberOfLines,
        prism,
        selectedSeriesNames,
      }),
    [rawSeries, numberOfLines, prism, selectedSeriesNames],
  );

  const visibleSeriesNameList = useMemo(() => Array.from(visibleSeriesNames), [visibleSeriesNames]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !baseSeriesNames.length) {
      return;
    }

    const applySelection = () => {
      applyLineChartSelectionToChart(chart, baseSeriesNames, selectedSeriesNames, {
        explicitVisibleNames: visibleSeriesNameList,
      });
    };

    applySelection();
    const rafId = requestAnimationFrame(applySelection);
    return () => cancelAnimationFrame(rafId);
  }, [baseSeriesNames, selectedSeriesNames, visibleSeriesNameList, chartReadyTick]);

  useEffect(() => {
    doneHideRef.current = false;
  }, [data, numberOfLines]);

  useEffect(() => {
    if (!onSeriesMetadataChange) return;
    const legendOptions = buildDoubleYAxisLegendOptions(rawSeries, DOUBLE_Y_AXIS_LEGEND_CONFIG);
    const seriesMeta = legendOptions.map(option => ({
      ...option,
      visible: option.name ? visibleSeriesNames.has(option.name) : false,
    }));
    onSeriesMetadataChange(seriesMeta);
  }, [rawSeries, onSeriesMetadataChange, visibleSeriesNames]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    updateSelectedSeriesFromChart(chart);
    // Only re-sync when the underlying series set changes, not on every selection update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSeries]);

  const preparedSeries = useMemo((): ChartSeriesConfig[] => {
    return rawSeries.map(series => {
      if (!series || !series.name) {
        return { ...series, compare: undefined, linkedTo: undefined } as ChartSeriesConfig;
      }

      const isVisible = visibleSeriesNames.has(series.name);

      return {
        ...series,
        visible: isVisible,
        showInLegend: true,
        showInNavigator: series.showInNavigator !== false,
        compare: undefined,
        linkedTo: undefined,
      } as ChartSeriesConfig;
    });
  }, [rawSeries, visibleSeriesNames]);

  const primaryAxisSeries = useMemo(
    () => preparedSeries.filter(series => (series.yAxis ?? PRIMARY_AXIS_ID) === PRIMARY_AXIS_ID),
    [preparedSeries],
  );

  const secondaryAxisSeries = useMemo(
    () => preparedSeries.filter(series => series.yAxis === SECONDARY_AXIS_ID),
    [preparedSeries],
  );

  const collectAxisValues = useCallback((axisSeries: ChartSeriesConfig[]) => {
    const values: number[] = [];
    axisSeries.forEach(series => {
      if (series.visible === false) return;
      normalizeSeriesData(series.data as PointLike[] | undefined).forEach(([, y]) => {
        if (Number.isFinite(y)) {
          values.push(y as number);
        }
      });
    });
    return values;
  }, []);

  const primaryAxisValues = useMemo(() => collectAxisValues(primaryAxisSeries), [collectAxisValues, primaryAxisSeries]);
  const secondaryAxisValues = useMemo(
    () => collectAxisValues(secondaryAxisSeries),
    [collectAxisValues, secondaryAxisSeries],
  );

  const primaryAxisFloorInfo = useMemo(() => deriveNonNegativeAxisFloor(primaryAxisSeries as any), [primaryAxisSeries]);
  const secondaryAxisFloorInfo = useMemo(
    () => deriveNonNegativeAxisFloor(secondaryAxisSeries as any),
    [secondaryAxisSeries],
  );
  const axisPaddingRatio = useMemo(
    () => (Number.isFinite(yAxisPaddingRatio) && yAxisPaddingRatio >= 0 ? yAxisPaddingRatio : 0.2),
    [yAxisPaddingRatio],
  );
  const primaryAxisLimits = useMemo(
    () => deriveAxisLimits(primaryAxisValues, axisPaddingRatio),
    [primaryAxisValues, axisPaddingRatio],
  );
  const secondaryAxisLimits = useMemo(
    () => deriveAxisLimits(secondaryAxisValues, axisPaddingRatio),
    [secondaryAxisValues, axisPaddingRatio],
  );

  const resolvedPrimaryMin = useMemo(() => {
    if (yAxisRangeMode === 'padded-visible') {
      return undefined;
    }
    if (typeof primaryAxisLimits.min === 'number') {
      return typeof primaryAxisFloorInfo.floor === 'number'
        ? Math.max(primaryAxisFloorInfo.floor, primaryAxisLimits.min)
        : primaryAxisLimits.min;
    }
    return typeof primaryAxisFloorInfo.floor === 'number' ? primaryAxisFloorInfo.floor : undefined;
  }, [primaryAxisLimits, primaryAxisFloorInfo.floor, yAxisRangeMode]);

  const resolvedPrimaryMax = useMemo(() => {
    if (yAxisRangeMode === 'padded-visible') {
      return undefined;
    }
    if (typeof primaryAxisLimits.max === 'number') {
      return primaryAxisLimits.max;
    }
    return undefined;
  }, [primaryAxisLimits, yAxisRangeMode]);

  const resolvedSecondaryMin = useMemo(() => {
    if (yAxisRangeMode === 'padded-visible') {
      return undefined;
    }
    if (typeof secondaryAxisLimits.min === 'number') {
      return typeof secondaryAxisFloorInfo.floor === 'number'
        ? Math.max(secondaryAxisFloorInfo.floor, secondaryAxisLimits.min)
        : secondaryAxisLimits.min;
    }
    return typeof secondaryAxisFloorInfo.floor === 'number' ? secondaryAxisFloorInfo.floor : undefined;
  }, [secondaryAxisLimits, secondaryAxisFloorInfo.floor, yAxisRangeMode]);

  const resolvedSecondaryMax = useMemo(() => {
    if (yAxisRangeMode === 'padded-visible') {
      return undefined;
    }
    if (typeof secondaryAxisLimits.max === 'number') {
      return secondaryAxisLimits.max;
    }
    return undefined;
  }, [secondaryAxisLimits, yAxisRangeMode]);

  const chartSeriesOptions = useMemo(
    () =>
      appendDoubleYAxisChartSeries(
        preparedSeries,
        DOUBLE_Y_AXIS_LEGEND_CONFIG,
        series => {
          const plottedData = normalizeSeriesData(series.data as PointLike[] | undefined);
          return {
            type: 'line',
            name: series.name,
            data: plottedData,
            yAxis: series.yAxis ?? PRIMARY_AXIS_ID,
            color: typeof series.color === 'string' ? series.color : undefined,
            visible: series.visible !== false,
            animation: false,
            lineWidth: 2,
            marker: { enabled: false },
            states: {
              inactive: { opacity: 1 },
              hover: { lineWidthPlus: 0 },
            },
            threshold: undefined,
            softThreshold: false,
            compare: undefined,
            linkedTo: undefined,
            dataGrouping: { enabled: false },
            boostThreshold: 0,
            turboThreshold: 0,
            cropThreshold: plottedData.length + 1,
            ...(series.custom?.description ? { custom: { description: series.custom.description } } : {}),
          };
        },
        { includeInlineLegendDivider: !dropdownLegendEnabled },
      ),
    [dropdownLegendEnabled, preparedSeries],
  );

  const showInlineLegendDivider = useMemo(() => {
    if (dropdownLegendEnabled) return false;
    const leftCount = preparedSeries.filter(series => (series.yAxis ?? PRIMARY_AXIS_ID) === PRIMARY_AXIS_ID).length;
    const rightCount = preparedSeries.filter(series => series.yAxis === SECONDARY_AXIS_ID).length;
    return leftCount > 0 && rightCount > 0;
  }, [dropdownLegendEnabled, preparedSeries]);

  useEffect(() => {
    if (!baseSeriesNames.length) {
      if (selectedSeriesNames !== null) {
        updateSelectedSeriesNames(null);
      }
      return;
    }

    if (selectedSeriesNames === null) {
      return;
    }

    if (!Array.isArray(selectedSeriesNames)) {
      updateSelectedSeriesNames(null);
      return;
    }

    if (selectedSeriesNames.length === 0) {
      return;
    }

    const baseNameSet = new Set(baseSeriesNames);
    const filtered = selectedSeriesNames.filter(name => baseNameSet.has(name));

    if (!filtered.length) {
      updateSelectedSeriesNames(null);
      return;
    }

    if (filtered.length === selectedSeriesNames.length) {
      return;
    }

    updateSelectedSeriesNames(filtered);
  }, [baseSeriesNames, selectedSeriesNames, updateSelectedSeriesNames]);

  useEffect(() => {
    const onScroll = () => {
      const elements = document.getElementsByClassName('xLabel');

      for (let i = 0; i < elements.length; i++) {
        (elements[i] as unknown as any).style.display = 'none';
      }
    };
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    function handleClick(e: any) {
      const file = (e.target as Element).getAttribute('download');
      if (file) {
        const fileType = file.substr(file.length - 3, 3);
        reactGA.event({ category: 'User', action: `Downloaded ${fileType}: ${file}` });
      }
    }
    window.addEventListener('click', handleClick);

    return function cleanupListener() {
      window.removeEventListener('click', handleClick);
    };
  }, [chartRef]);

  useEffect(() => {
    chartRef.current.reflow();
  }, [chartRef, size]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || typeof chart.setTitle !== 'function') {
      return;
    }
    chart.setTitle({ text: chartTitle ?? '' }, undefined, false);
  }, [chartTitle]);

  const lastAppliedRangeRef = useRef<unknown>(null);
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    chart.redraw();

    if (lastAppliedRangeRef.current !== selectedRangeSelector) {
      setXRangeUserCustom(false);
      applyingRangePresetRef.current = true;
      const anchorFromButton = pendingDurationPresetAnchorRef.current;
      pendingDurationPresetAnchorRef.current = undefined;
      const applied = applyRangeSelectorToChart(chart, selectedRangeSelector, anchorFromButton);
      if (applied) {
        lastAppliedRangeRef.current = selectedRangeSelector;
        savedUserXExtentsRef.current = null;
      }
      requestAnimationFrame(() => {
        applyingRangePresetRef.current = false;
      });
    } else {
      const xAxis = chart.xAxis?.[0];
      const saved = savedUserXExtentsRef.current;
      if (saved && xAxis) {
        requestAnimationFrame(() => {
          const cur = readFiniteXExtentsFromAxis(xAxis);
          if (cur && xExtentsDiffer(cur, saved)) {
            xAxis.setExtremes(saved.min, saved.max, false, false);
          }
        });
      }
    }
  }, [selectedRangeSelector, chartRef, rawSeries]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || xRangeUserCustom) return;

    const xValues = extractSeriesXValues(chart.series || []);
    if (!xValues.length) return;

    const xAxis = chart.xAxis?.[0];
    if (!xAxis || typeof xAxis.getExtremes !== 'function') return;

    const { min, max } = xAxis.getExtremes();
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    if (areChartXExtremesCanonicalForRangeIndex(chart, selectedRangeSelector, min, max)) return;

    applyingRangePresetRef.current = true;
    applyRangeSelectorToChart(chart, selectedRangeSelector);
    requestAnimationFrame(() => {
      applyingRangePresetRef.current = false;
    });
  }, [rawSeries, selectedRangeSelector, xRangeUserCustom]);

  const updateYAxesForVisibleWindow = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const xAxis = chart.xAxis?.[0];
    const primaryAxis = chart.yAxis?.[0];
    const secondaryAxis = chart.yAxis?.[1];
    if (!xAxis || !primaryAxis || !secondaryAxis) return;

    if (yAxisRangeMode !== 'padded-visible') {
      primaryAxis.setExtremes(undefined, undefined, false, false);
      secondaryAxis.setExtremes(undefined, undefined, false, false);
      chart.redraw();
      return;
    }

    const xExtremes = xAxis.getExtremes();
    const minX = xExtremes?.min;
    const maxX = xExtremes?.max;
    const paddingRatio = Number.isFinite(yAxisPaddingRatio) && yAxisPaddingRatio >= 0 ? yAxisPaddingRatio : 0.2;

    const collectWindowValues = (axis: HighStock.Axis) =>
      (Array.isArray(axis.series) ? axis.series : [])
        .filter(
          (series: any) =>
            series &&
            series.name &&
            !/^Navigator \d+$/.test(series.name) &&
            !isLegendAxisDivider(series.name) &&
            series.visible !== false,
        )
        .flatMap((series: any) => {
          const seriesData = Array.isArray(series.options?.data) ? (series.options.data as PointLike[]) : [];
          const normalizedSeriesData: Array<[number, number | null]> = normalizeSeriesData(seriesData);
          return normalizedSeriesData
            .filter(
              ([x]: [number, number | null]) =>
                !Number.isFinite(minX) || !Number.isFinite(maxX) || (x >= minX && x <= maxX),
            )
            .map(([, y]: [number, number | null]) => y)
            .filter((value: number | null): value is number => Number.isFinite(value));
        });

    const primaryLimits = deriveAxisLimits(collectWindowValues(primaryAxis), paddingRatio);
    const secondaryLimits = deriveAxisLimits(collectWindowValues(secondaryAxis), paddingRatio);

    if (typeof primaryLimits.min === 'number' && typeof primaryLimits.max === 'number') {
      primaryAxis.setExtremes(primaryLimits.min, primaryLimits.max, false, false);
    } else {
      primaryAxis.setExtremes(undefined, undefined, false, false);
    }
    if (typeof secondaryLimits.min === 'number' && typeof secondaryLimits.max === 'number') {
      secondaryAxis.setExtremes(secondaryLimits.min, secondaryLimits.max, false, false);
    } else {
      secondaryAxis.setExtremes(undefined, undefined, false, false);
    }
    chart.redraw();
  }, [yAxisPaddingRatio, yAxisRangeMode]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      updateYAxesForVisibleWindow();
    });
    return () => cancelAnimationFrame(rafId);
  }, [preparedSeries, selectedRangeSelector, updateYAxesForVisibleWindow]);

  useEffect(() => {
    const chart = chartRef.current;
    const xAxis = chart?.xAxis?.[0];
    if (!chart || !xAxis) return;

    const handleAfterSetExtremes = (e: any) => {
      const picked = pickXExtentsFromAfterSetExtremesEvent(e);
      if (picked && shouldPersistUserXExtentsFromEvent(e, { applyingRangePreset: applyingRangePresetRef.current })) {
        savedUserXExtentsRef.current = picked;
      }
      const chartInstance = chartRef.current;
      const evMin = e?.min;
      const evMax = e?.max;
      if (
        !applyingRangePresetRef.current &&
        chartInstance &&
        Number.isFinite(evMin) &&
        Number.isFinite(evMax) &&
        !areChartXExtremesCanonicalForRangeIndex(chartInstance, selectedRangeSelector, evMin, evMax)
      ) {
        setXRangeUserCustom(true);
      }
      requestAnimationFrame(() => {
        updateYAxesForVisibleWindow();
      });
    };

    HighStock.addEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    return () => {
      HighStock.removeEvent(xAxis, 'afterSetExtremes', handleAfterSetExtremes as any);
    };
  }, [preparedSeries, selectedRangeSelector, updateYAxesForVisibleWindow]);

  useEffect(() => {
    if (!onExportMenuVisibilityChange) return;

    let cleanup: (() => void) | null = null;
    let disposed = false;

    const setup = () => {
      if (disposed) return;
      const chart = chartRef.current;
      if (!chart) {
        requestAnimationFrame(setup);
        return;
      }

      const doc = chart.renderTo?.ownerDocument;
      if (!doc) return;

      const body = doc.body;
      if (!body) return;

      let lastVisible: boolean | null = null;
      const menuSelector = `.${exportMenuClassName}`;
      const computeVisibility = () => {
        const menu = body.querySelector(menuSelector) as HTMLElement | null;
        if (!menu) {
          return false;
        }
        const view = doc.defaultView;
        const style = view ? view.getComputedStyle(menu) : null;
        if (style) {
          if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
            return false;
          }
        } else if (menu.style.display === 'none') {
          return false;
        }

        return menu.childElementCount > 0;
      };

      const emitIfChanged = () => {
        const visible = computeVisibility();
        if (visible !== lastVisible) {
          lastVisible = visible;
          onExportMenuVisibilityChange(visible);
        }
      };

      emitIfChanged();

      const observer = new MutationObserver(() => {
        emitIfChanged();
      });

      observer.observe(body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style'],
      });

      const handleInteraction = () => {
        requestAnimationFrame(emitIfChanged);
      };

      body.addEventListener('click', handleInteraction, true);
      body.addEventListener('keydown', handleInteraction, true);

      cleanup = () => {
        observer.disconnect();
        body.removeEventListener('click', handleInteraction, true);
        body.removeEventListener('keydown', handleInteraction, true);
        onExportMenuVisibilityChange(false);
      };
    };

    setup();

    return () => {
      disposed = true;
      if (cleanup) {
        cleanup();
        cleanup = null;
      }
    };
  }, [exportMenuClassName, onExportMenuVisibilityChange]);

  const normalizedTopicForExplainability = useMemo(() => {
    if (!topicForExplainability) return '';
    return String(topicForExplainability).trim();
  }, [topicForExplainability]);

  const chartOptions = useMemo(() => {
    const { floor: primaryAxisFloor } = primaryAxisFloorInfo;
    const { floor: secondaryAxisFloor } = secondaryAxisFloorInfo;
    const legendY = layoutSeriesCount < 3 ? (chartSubtitle ? 42 : 24) : -14;
    const isDualSeriesHorizontalLegend = !dropdownLegendEnabled && layoutSeriesCount === 2;
    const inlineLegend = !dropdownLegendEnabled && layoutSeriesCount > 1;
    const inlineLegendRightAxisShift = inlineLegend ? INLINE_LEGEND_RIGHT_AXIS_SHIFT : 0;
    const {
      marginRight,
      legendX,
      verticalInlineLegend,
      rightAxisMargin,
      rightAxisOffset,
      rightAxisLabelAlign,
      rightAxisLabelX,
      spacingRight,
      legendMargin,
    } = resolveDoubleYAxisChartLayout({
      dropdownLegendEnabled,
      layoutSeriesCount,
      defaultMarginRight: 70,
      leftAxisMargin: 30,
      leftAxisOffset: 30,
    });
    const chartMarginLeft = 70;
    const chartMarginRight = verticalInlineLegend ? marginRight - INLINE_VERTICAL_PLOT_EXTRA_WIDTH : marginRight;
    const chartLegendX = verticalInlineLegend ? (legendX ?? 28) : legendX;
    const secondaryAxisOffset = verticalInlineLegend
      ? Math.max(0, (rightAxisOffset ?? 0) - INLINE_VERTICAL_PLOT_EXTRA_WIDTH)
      : isDualSeriesHorizontalLegend
        ? DUAL_SERIES_RIGHT_AXIS_OFFSET
        : undefined;
    const secondaryAxisLabelX = verticalInlineLegend
      ? (rightAxisLabelX ?? 36) - INLINE_VERTICAL_PLOT_EXTRA_WIDTH
      : rightAxisLabelX;
    const resolvedSecondaryAxisOffset = (secondaryAxisOffset ?? 30) + inlineLegendRightAxisShift;
    const resolvedSecondaryAxisLabelX = (secondaryAxisLabelX ?? -10) + inlineLegendRightAxisShift;
    const secondaryAxisTitleX = -17 + inlineLegendRightAxisShift;

    return {
      chart: {
        className: 'double-y-axis-chart',
        marginLeft: chartMarginLeft,
        marginRight: chartMarginRight,
        spacingRight: spacingRight ?? 16,
        marginTop: chartSubtitle ? 120 : 116,
        height: (gridHeight / 4) * 446 + (gridHeight / 4 - 1) * 38,
        animation: false,
        style: {
          fontFamily: ['Montserrat', 'sans-serif'],
          left: '0px',
        },
        events: {
          load: function (this: HighStock.Chart) {
            if (showInlineLegendDivider) {
              maintainDoubleYAxisInlineLegendDivider(this);
            }
            syncLineChartLegendTooltips(this, tooltipCfg);
            if (!dropdownLegendEnabled) {
              scheduleLineChartSelectedSeriesSync(this, updateSelectedSeriesFromChart);
            }
          },
          render: function (this: HighStock.Chart) {
            if (showInlineLegendDivider) {
              maintainDoubleYAxisInlineLegendDivider(this);
            }
            syncLineChartLegendTooltips(this, tooltipCfg);
          },
        },
      },
      boost: { enabled: false },
      xAxis: {
        type: 'datetime',
        ordinal: false,
        startOnTick: false,
        endOnTick: false,
      },
      rangeSelector: {
        buttonSpacing: 12,
        inputEnabled: false,
        selected: xRangeUserCustom ? null : (selectedRangeSelector ?? DEFAULT_RANGE_SELECTOR_INDEX),
        floating: true,
        labelStyle: { display: 'none' },
        dropdown: 'never',
        x: 0,
        y: -24,
        buttonTheme: {
          r: 15,
          fill: 'transparent',
          height: 26,
          style: {
            fill: 'transparent',
            fontSize: 13,
            marginRight: 15,
            color: '#33485E',
            background: 'transparent',
            fontWeight: 500,
          },
          states: {
            hover: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 500,
              },
            },
            select: {
              fill: 'transparent',
              style: {
                color: '#0081C4',
                background: 'transparent',
                fontWeight: 600,
              },
            },
          },
        },
        buttonPosition: {
          x: -48,
          y: -78,
        },
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M',
            title: 'View 1 month',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(0);
              },
            },
          },
          {
            type: 'month',
            count: 3,
            text: '3M',
            title: 'View 3 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(1);
              },
            },
          },
          {
            type: 'month',
            count: 6,
            text: '6M',
            title: 'View 6 months',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(2);
              },
            },
          },
          {
            type: 'ytd',
            text: 'YTD',
            title: 'View year to date',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(3);
              },
            },
          },
          {
            type: 'year',
            count: 1,
            text: '1Y',
            title: 'View 1 year',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = readMainAxisMaxForDurationPreset(chartRef.current);
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(4);
              },
            },
          },
          {
            type: 'all',
            text: 'Max',
            title: 'View all',
            events: {
              click: () => {
                pendingDurationPresetAnchorRef.current = undefined;
                setXRangeUserCustom(false);
                setSelectedRangeSelector?.(MAX_RANGE_SELECTOR_INDEX);
              },
            },
          },
        ],
      },
      exporting: {
        filename: exportFileName,
        csv: {
          columnHeaderFormatter: function (item: any) {
            if (item && item instanceof HighStock.Axis) {
              if ((item as unknown as any).type === 'datetime') {
                return 'Date';
              }
              return 'Category';
            }
            return {
              columnTitle: item.name,
            };
          },
          dateFormat: '%m/%d/%Y',
        },
        enabled: true,
        buttons: {
          contextButton: {
            x: 0,
            y: 2,
            symbol: 'ellipsisvertical',
            menuClassName: combinedMenuClassName,
            menuItems: getLineChartExportMenuItems(baseSeriesNames.length),
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        x: -100,
        menuItemHoverStyle: {
          background: '#EAF2FF',
        },
        menuItemStyle: {
          height: 30,
          fill: '#33485E',
          fontSize: 15,
          padding: '6px 12px',
        },
      },
      title: {
        text: chartTitle,
        align: 'left',
        x: 12,
        y: 24,
        style: {
          fontSize: '18px',
          color: 'var(--color-gray-6)',
          fontWeight: 500,
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      subtitle: {
        text: '',
        align: 'left',
        x: 12,
        y: 49,
        style: {
          color: 'var(--color-gray-6)',
          fontWeight: 'normal',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: !dropdownLegendEnabled && layoutSeriesCount > 1 && !isLoading,
        floating: layoutSeriesCount < 3,
        layout: layoutSeriesCount < 3 ? 'horizontal' : 'vertical',
        align: 'right',
        verticalAlign: 'top',
        x: chartLegendX ?? (layoutSeriesCount < 3 ? -30 : -10),
        y: legendY,
        maxHeight: 250,
        reversed: false,
        margin: legendMargin ?? (!dropdownLegendEnabled ? 8 : undefined),
        useHTML: showInlineLegendDivider,
        labelFormatter: showInlineLegendDivider ? doubleYAxisLegendLabelFormatter : undefined,
        itemHiddenStyle: { color: '#666666', textDecoration: 'none' },
        events: {
          itemClick: legendEventsItemClick,
        },
      },
      yAxis: [
        {
          id: PRIMARY_AXIS_ID,
          linkedTo: undefined,
          title: {
            text: chartYtitle,
            x: 16,
            style: {
              color: 'var(--color-gray-6)',
              fontWeight: 'normal',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
          opposite: false,
          offset: 30,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: 'var(--color-gray-6)',
            },
            align: 'right',
            x: 10,
            y: -6,
            allowOverlap: true,
          },
          tickPixelInterval: 55,
          showFirstLabel: true,
          showLastLabel: true,
          margin: 30,
          ...(yAxisRangeMode !== 'padded-visible' && primaryAxisFloor !== undefined
            ? {
                floor: primaryAxisFloor,
                softMin: primaryAxisFloor,
                minPadding: 0,
              }
            : {}),
          ...(typeof resolvedPrimaryMin === 'number' || typeof resolvedPrimaryMax === 'number'
            ? {
                ...(typeof resolvedPrimaryMin === 'number' ? { min: resolvedPrimaryMin } : {}),
                ...(typeof resolvedPrimaryMax === 'number' ? { max: resolvedPrimaryMax } : {}),
                startOnTick: false,
                endOnTick: false,
              }
            : {}),
        },
        {
          id: SECONDARY_AXIS_ID,
          linkedTo: undefined,
          title: {
            text: chartYtitle2,
            x: secondaryAxisTitleX,
            style: {
              color: 'var(--color-gray-6)',
              fontWeight: 'normal',
              letterSpacing: '0.5px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
          opposite: true,
          offset: resolvedSecondaryAxisOffset,
          plotLines: [
            {
              value: 0,
              width: 2,
              color: 'silver',
            },
          ],
          labels: {
            format: '{value}',
            style: {
              color: 'var(--color-gray-6)',
            },
            align: rightAxisLabelAlign ?? 'right',
            x: resolvedSecondaryAxisLabelX,
            y: -6,
            allowOverlap: true,
          },
          tickPixelInterval: 55,
          showFirstLabel: true,
          showLastLabel: true,
          margin: rightAxisMargin ?? 30,
          plotBands: plotBands,
          ...(yAxisRangeMode !== 'padded-visible' && secondaryAxisFloor !== undefined
            ? {
                floor: secondaryAxisFloor,
                softMin: secondaryAxisFloor,
                minPadding: 0,
              }
            : {}),
          ...(typeof resolvedSecondaryMin === 'number' || typeof resolvedSecondaryMax === 'number'
            ? {
                ...(typeof resolvedSecondaryMin === 'number' ? { min: resolvedSecondaryMin } : {}),
                ...(typeof resolvedSecondaryMax === 'number' ? { max: resolvedSecondaryMax } : {}),
                startOnTick: false,
                endOnTick: false,
              }
            : {}),
        },
      ],
      tooltip: {
        ...lineChartHighchartsTooltipCfg(tooltipCfg),
      },
      lang: {
        noData: '',
      },
      noData: {
        style: {
          fontWeight: 'bold',
          fontSize: '15px',
          color: '#303030',
        },
      },
      series: chartSeriesOptions,
      plotOptions: {
        line: {
          softThreshold: false,
        },
        series: {
          selected: false,
          showInNavigator: true,
          compare: undefined,
          dataGrouping: { enabled: false },
          turboThreshold: 0,
          marker: { enabled: false },
          linkedTo: undefined,
          animation: false,
          line: {
            selected: false,
          },
          point: {
            events: {
              click: function () {
                if (typeof onTopicExplainabilityRequest === 'function' && normalizedTopicForExplainability) {
                  onTopicExplainabilityRequest(normalizedTopicForExplainability);
                }
              },
            },
          },
          events: {
            afterAnimate: function (event: HighStock.SeriesAfterAnimateCallbackFunction) {
              const chart = (event as { target?: { chart?: HighStock.Chart } }).target?.chart;
              if (!chart || doneHideRef.current) {
                return;
              }

              const exportableSeries =
                chart.series?.filter(
                  series => series.name && !/^Navigator \d+$/.test(series.name) && !isLegendAxisDivider(series.name),
                ) ?? [];
              if (!exportableSeries.length) {
                return;
              }

              applyLineChartSelectionToChart(chart, baseSeriesNames, selectedSeriesNames, {
                explicitVisibleNames: Array.from(visibleSeriesNames),
              });
              doneHideRef.current = true;
              updateSelectedSeriesFromChart(chart);
            },
          },
        },
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 500,
            },
            chartOptions: {
              yAxis: [
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  labels: {
                    align: 'right',
                    x: 20,
                    y: -6,
                  },
                  showLastLabel: false,
                },
                {
                  visible: false,
                },
              ],
            },
          },
        ],
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartTitle,
    chartSubtitle,
    chartYtitle,
    chartYtitle2,
    showInlineLegendDivider,
    chartSeriesOptions,
    combinedMenuClassName,
    dropdownLegendEnabled,
    exportFileName,
    gridHeight,
    layoutSeriesCount,
    normalizedTopicForExplainability,
    numberOfLines,
    onTopicExplainabilityRequest,
    plotBands,
    preparedSeries,
    primaryAxisFloorInfo,
    prism,
    resolvedPrimaryMax,
    resolvedPrimaryMin,
    secondaryAxisFloorInfo,
    resolvedSecondaryMax,
    resolvedSecondaryMin,
    selectedSeriesNames,
    tooltipCfg,
    selectedRangeSelector,
    xRangeUserCustom,
    setSelectedRangeSelector,
    updateSelectedSeriesFromChart,
    legendEventsItemClick,
    visibleSeriesNames,
    yAxisRangeMode,
    isLoading,
    isNarrowChart,
  ]);

  return (
    <Root $gridheight={gridHeight}>
      <HighchartContainer>
        <HighchartsReact
          containerProps={{ style: { width: '100%', height: '100%' } }}
          highcharts={HighStock}
          constructorType={'stockChart'}
          options={chartOptions ?? EMPTY_STOCK_OPTIONS}
          callback={(chart: any) => {
            chartRef.current = chart;
            setChartReadyTick(tick => tick + 1);
          }}
        />
      </HighchartContainer>
    </Root>
  );
}
